// The Talk turn machine, pure: it turns microphone events (speech
// detected, a block of audio, speech over, a misfire — from the VAD or
// from a held key) and socket events (the answer finished, the turn
// failed) into the commands the session Up carries, in the contract's
// order — one turn at a time, so speech that ends while an answer is
// still coming is held and sent the moment the socket is free (the
// queue; the server refuses a second turn while one runs).
//
// Audio streams while it is spoken when no turn is running — the
// recogniser is mostly done by the time the silence confirms the
// turn's end, which is the whole latency win of the open microphone.
// While an answer runs, the blocks are held instead and flushed as one
// burst at its end.
export type TurnCmd =
  | { t: "turnStart"; turn: string; mode: "chat" }
  | { t: "format"; rate: number }
  | { t: "audio"; pcm: Uint8Array }
  | { t: "over"; turn: string }
  | { t: "cancel"; turn: string };

export type TurnEv =
  // Speech began (the VAD said so, or a key went down): the pre-roll
  // blocks follow as block events right after.
  | { t: "speechStart"; rate: number }
  // The silence confirmed the turn's end (or the key came up).
  | { t: "speechEnd" }
  // The detector took it back: not speech after all.
  | { t: "misfire" }
  // One batch of microphone audio, pcm16 at the device's rate.
  | { t: "block"; pcm: Uint8Array }
  // The socket's turn finished (Down finished).
  | { t: "finished" }
  // The socket's turn failed (Down error), or its start was refused.
  | { t: "failed" }
  // The user dropped it by hand (the screen's stop): everything of
  // this turn goes, held speech too.
  | { t: "abandon" }
  // The socket is gone (dropped, or a turn that could not be sent):
  // nothing can go out, so everything is dropped — the running turn,
  // held speech, an ongoing turn's own speech — and nothing is sent,
  // not even a cancel. The screen feeds this when the session is not
  // live; without it the machine would wait for a finished that can
  // never come and the queue would never drain.
  | { t: "offline" };

export type TalkStatus =
  // Nothing running and nothing held.
  | "idle"
  // A turn is open on the socket, its audio streaming.
  | "speaking"
  // The turn's end is sent; the answer is coming.
  | "answering"
  // Speech was held while the answer ran; it goes out when free.
  | "queued";

export interface TurnMachine {
  status(): TalkStatus;
  // The socket's running turn id, "" when none (what Down frames and
  // a cancel are addressed to).
  turn(): string;
  ev(e: TurnEv): TurnCmd[];
}

export function createTurnMachine(ids: () => string = newTurnId): TurnMachine {
  let status: TalkStatus = "idle";
  let turn = "";
  // Held blocks while an answer runs, with the rate they were taken
  // at (the format goes out once per turn, before its first audio).
  let held: Uint8Array[] = [];
  let heldRate = 0;
  let speaking = false; // speech is being taken (mic side)

  return {
    status: () => status,
    turn: () => turn,
    ev(e) {
      switch (e.t) {
        case "speechStart": {
          speaking = true;
          if (status === "idle") {
            turn = ids();
            status = "speaking";
            return [
              { t: "turnStart", turn, mode: "chat" },
              { t: "format", rate: e.rate },
            ];
          }
          // An answer is coming: the blocks are held, not streamed, at
          // the rate this speech was taken at.
          heldRate = e.rate;
          return [];
        }
        case "block": {
          // Only a taken speech's blocks go anywhere: a trailing batch
          // after the turn's over is nobody's.
          if (!speaking) return [];
          if (status === "speaking") return [{ t: "audio", pcm: e.pcm }];
          // Speech that cannot stream yet (an answer runs, or the
          // socket went free mid-speech) waits to be its own turn.
          held.push(e.pcm);
          return [];
        }
        case "speechEnd": {
          speaking = false;
          if (status === "speaking") {
            status = "answering";
            return [{ t: "over", turn }];
          }
          if (held.length > 0) status = "queued";
          return [];
        }
        case "misfire": {
          speaking = false;
          if (status === "speaking") {
            const dead = turn;
            turn = "";
            status = "idle";
            return [{ t: "cancel", turn: dead }];
          }
          held = [];
          return [];
        }
        case "abandon": {
          speaking = false;
          held = [];
          if (status === "idle") return [];
          const dead = turn;
          turn = "";
          status = "idle";
          return dead ? [{ t: "cancel", turn: dead }] : [];
        }
        case "offline": {
          speaking = false;
          held = [];
          turn = "";
          status = "idle";
          return [];
        }
        case "finished":
        case "failed": {
          if (status === "speaking") {
            // The turn died mid-speech (an error Down, or the server's
            // own end of the utterance). The speech goes with it: its
            // blocks were the dead turn's — streamed already, not held
            // — and holding what comes next would wait for a finished
            // that nothing will send. The machine is idle the moment
            // the user can speak again; the mic's taking ends with
            // this (the screen watches for it).
            speaking = false;
            turn = "";
            status = "idle";
            return [];
          }
          turn = "";
          status = "idle";
          if (held.length === 0) return [];
          // Held speech goes out the moment the socket frees. Still
          // being spoken, it OPENS as a live turn — held blocks first,
          // the rest streaming — so its end is its own over, not one
          // sent into the middle of a word.
          const blocks = held;
          const rate = heldRate;
          held = [];
          turn = ids();
          const out: TurnCmd[] = [{ t: "turnStart", turn, mode: "chat" }];
          if (rate > 0) out.push({ t: "format", rate });
          for (const pcm of blocks) out.push({ t: "audio", pcm });
          if (speaking) {
            status = "speaking";
            return out;
          }
          status = "answering";
          out.push({ t: "over", turn });
          return out;
        }
      }
    },
  };
}

// Turn ids are minted by the client and unique per user; a random UUID
// is that on one device (the session client's own rule).
export function newTurnId(): string {
  const c = globalThis.crypto;
  if (c && "randomUUID" in c) return c.randomUUID();
  return `turn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
